export class ChatRequestError extends Error {
  constructor(message: string, public code?: string, public retryAfter?: number) {
    super(message);
    this.name = "ChatRequestError";
  }
}

export class RequestTimeoutError extends Error {
  constructor() {
    super("The connection stopped responding. Reconnecting…");
    this.name = "RequestTimeoutError";
  }
}

/** Only bounds response headers. The stream has a separate idle deadline. */
export async function fetchWithDeadline(input: RequestInfo | URL, init: RequestInit = {}, timeoutMs = 45_000): Promise<Response> {
  const controller = new AbortController();
  const parent = init.signal;
  const abort = () => controller.abort(parent?.reason);
  if (parent?.aborted) abort();
  else parent?.addEventListener("abort", abort, { once: true });
  let expired = false;
  const timer = setTimeout(() => { expired = true; controller.abort(); }, timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } catch (error) {
    if (expired && !parent?.aborted) throw new RequestTimeoutError();
    throw error;
  } finally {
    clearTimeout(timer);
    parent?.removeEventListener("abort", abort);
  }
}

export function getErrorAction(code?: string): "account" | "model" | "reconnect" | "retry" | "stop" {

  if (code === "image_cancel_pending" || code === "cancel_failed") return "stop";
  if (code === "oauth_expired" || code === "unauthorized") return "account";
  if (code === "upstream_capacity" || code === "model_unavailable") return "model";
  if (code === "network_error") return "reconnect";
  return "retry";
}

/** Error bodies can stall too; do not let an incomplete JSON body block Stop or recovery. */
export async function readResponseJson(response: Response, signal?: AbortSignal, timeoutMs = 45_000): Promise<unknown> {
  if (!response.body) return null;
  const reader = response.body.getReader();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let onAbort: (() => void) | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new RequestTimeoutError()), timeoutMs);
    onAbort = () => reject(new DOMException("Aborted", "AbortError"));
    if (signal?.aborted) onAbort();
    else signal?.addEventListener("abort", onAbort, { once: true });
  });
  try {
    return await Promise.race([(async () => {
      const decoder = new TextDecoder(); let text = "";
      while (true) {
        const { value, done } = await reader.read();
        text += decoder.decode(value, { stream: !done });
        if (text.length > 1024 * 1024) return null;
        if (done) break;
      }
      try { return JSON.parse(text) as unknown; } catch { return null; }
    })(), deadline]);
  } finally {
    clearTimeout(timer);
    if (onAbort) signal?.removeEventListener("abort", onAbort);
    void reader.cancel().catch(() => undefined);
  }
}
